import React from 'react';
import { usePortalAuth } from './PortalAuth';
import { ORGANISATIONS } from '../data/portal';
import type { OrgRole } from '../types/portal';

/**
 * The organisation the person is currently working in. The URL is the source of
 * truth inside /portal/orgs/:orgId; this remembers the last one so the sidebar
 * and the dashboard links land in the right place.
 */

interface PortalOrgValue {
  orgId: string | null;
  role: OrgRole | null;
  orgName: string;
  setOrgId: (orgId: string) => void;
}
const PortalOrgContext = React.createContext<PortalOrgValue | null>(null);
const STORAGE_KEY = 'cavalla.portal.org';
export function orgName(orgId: string | null): string {
  return ORGANISATIONS.find(org => org.id === orgId)?.name ?? 'Organisation';
}
export function PortalOrgProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    memberships,
    roleIn
  } = usePortalAuth();
  const [stored, setStored] = React.useState<string | null>(() => window.localStorage.getItem(STORAGE_KEY));

  // Fall back to the first membership if the remembered organisation is no longer available.
  const orgId = stored && memberships.some(m => m.orgId === stored) ? stored : memberships[0]?.orgId ?? null;
  const value = React.useMemo<PortalOrgValue>(() => ({
    orgId,
    role: orgId ? roleIn(orgId) : null,
    orgName: orgName(orgId),
    setOrgId: next => {
      window.localStorage.setItem(STORAGE_KEY, next);
      setStored(next);
    }
  }), [orgId, roleIn]);
  return <PortalOrgContext.Provider value={value} data-id="element-1438" data-mp="3foy5273jq2l5">{children}</PortalOrgContext.Provider>;
}
export function usePortalOrg(): PortalOrgValue {
  const context = React.useContext(PortalOrgContext);
  if (!context) throw new Error('usePortalOrg must be used inside PortalOrgProvider');
  return context;
}